import { Dialog, Loader, Typography } from '@pwskills/rachnaui';
import { classNames } from '@pwskills/rachnaui/utils';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { sendSkVideoInteractionEvent } from '@/utils/GAEventFunction';
import { formatHMS } from '@/components/common/video/utils';
import { dataLayer } from '../../../utils/common/dataLayer.utils';
import CourseDescriptionContext from '../../../context/CourseDescriptionContext';
import CategoryPageContext from '../../../context/CategoryPageContext';

type Props = {
  title: string;
  videoUrl: string;
  modalOpen: boolean;
  setModelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  type?: string;
  description?: string;
  section?: string;
};

const VideoModal = (props: Props) => {
  const { videoUrl, modalOpen, setModelOpen, title, type, description, section } = props;
  const iframeRef = useRef<any>(false);
  const playerContainerRef = useRef<HTMLDivElement | null>(null);
  const durationRef = useRef<number | undefined>(undefined);
  const currentTimeRef = useRef<number>(0);
  const playbackSpeedRef = useRef<number>(1);
  const isMutedRef = useRef<boolean>(false);
  const lastFullscreenRef = useRef<boolean>(false);
  const fullscreenListenerAttachedRef = useRef<boolean>(false);
  const volumeListenerAttachedRef = useRef<boolean>(false);
  const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });
  const router = useRouter();

  const { courseDetails } = useContext(CourseDescriptionContext);
  const { categoryDetails } = useContext(CategoryPageContext);

  useEffect(() => {
    if (!modalOpen) {
      iframeRef.current = false; // Reset iframeRef when modal is closed
    }
  }, [modalOpen]);

  const sendDataLayerEvent = () => {
    const dataLayerObj = {
      event: 'video_play_click',
      section_name: section,
      course_id: courseDetails?._id || categoryDetails?._id || '',
      video_url: videoUrl,
    };
    dataLayer(dataLayerObj);
  };

  const getDerivedVideoId = useCallback(() => {
    try {
      const parsed = new URL(videoUrl);
      return (
        parsed.searchParams.get('v') ||
        parsed.pathname.split('/').filter(Boolean).pop() ||
        undefined
      );
    } catch (_error) {
      return videoUrl?.split('/').pop();
    }
  }, [videoUrl]);

  const getUserPseudoIdFromCookie = (): string | undefined => {
    if (typeof document === 'undefined') return undefined;
    const gaCookie = document.cookie.split('; ').find((row) => row.startsWith('_ga='));
    return gaCookie?.split('=')[1];
  };

  const roundTo = (value: number | undefined, decimals = 2): number | undefined => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  };

  const emitVideoInteraction = useCallback(
    (featureName: 'speed' | 'fullscreen' | 'mute', featureValue: string | number) => {
      const currentTime = currentTimeRef.current || 0;
      const isFullscreen = !!document.fullscreenElement;
      sendSkVideoInteractionEvent({
        page_path: router.asPath,
        page_referrer_cta: (router.query?.clickText as string) || '',
        page_referrer_widget_name: (router.query?.section as string) || section || '',
        feature_category: 'video',
        feature_name: featureName,
        feature_value: featureValue,
        user_pseudo_id: getUserPseudoIdFromCookie(),
        lesson_id: (router.query?.lessonId as string) || '',
        milestone_id: (router.query?.milestoneId as string) || '',
        video_id: getDerivedVideoId(),
        video_type: type === 'emi' ? 'recorded' : undefined,
        video_duration_sec: formatHMS(durationRef.current),
        video_current_time_sec: formatHMS(currentTime),
        video_playback_speed: roundTo(playbackSpeedRef.current || 1),
        is_fullscreen: isFullscreen,
        is_muted: isMutedRef.current,
        course_batch_name: courseDetails?.name || courseDetails?.title || categoryDetails?.title,
        course_batch_slug: courseDetails?.slug || categoryDetails?.slug,
        course_batch_category: categoryDetails?.title,
        course_batch_id: courseDetails?._id || categoryDetails?._id,
        course_batch_cohort_id:
          (router.query?.cohortId as string) || (router.query?.year as string) || undefined,
        is_free: !!courseDetails?.pricing?.isFree,
      });
    },
    [
      router.asPath,
      router.query?.clickText,
      router.query?.section,
      router.query?.lessonId,
      router.query?.milestoneId,
      router.query?.cohortId,
      router.query?.year,
      section,
      type,
      getDerivedVideoId,
      courseDetails?.name,
      courseDetails?.title,
      courseDetails?.slug,
      courseDetails?._id,
      courseDetails?.pricing?.isFree,
      categoryDetails?.title,
      categoryDetails?.slug,
      categoryDetails?._id,
    ]
  );

  useEffect(() => {
    if (!modalOpen || fullscreenListenerAttachedRef.current) return;

    const handleFullscreenChange = () => {
      const isFullscreen = !!document.fullscreenElement;
      if (isFullscreen !== lastFullscreenRef.current) {
        lastFullscreenRef.current = isFullscreen;
        emitVideoInteraction('fullscreen', isFullscreen ? 'enter' : 'exit');
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    fullscreenListenerAttachedRef.current = true;
  }, [modalOpen, emitVideoInteraction]);

  useEffect(() => {
    if (!modalOpen || volumeListenerAttachedRef.current) return;

    const attachVolumeListener = () => {
      const mediaElement = playerContainerRef.current?.querySelector(
        'video'
      ) as HTMLVideoElement | null;
      if (!mediaElement) return undefined;

      const handleVolumeChange = () => {
        const nextMutedState = !!mediaElement.muted;
        if (nextMutedState !== isMutedRef.current) {
          isMutedRef.current = nextMutedState;
          emitVideoInteraction('mute', nextMutedState ? 'muted' : 'unmuted');
        }
      };

      mediaElement.addEventListener('volumechange', handleVolumeChange);
      volumeListenerAttachedRef.current = true;
      return undefined;
    };

    window.setTimeout(() => {
      attachVolumeListener();
    }, 0);
  }, [modalOpen, emitVideoInteraction]);

  const onPlay = () => {
    if (!iframeRef.current) {
      iframeRef.current = true;
      sendDataLayerEvent();
    }
  };

  return (
    <Dialog
      open={modalOpen}
      closeModal={() => setModelOpen(false)}
      title={title}
      isOutSideClick
      titleClass="font-[700]"
      className="w-[90%] regular:!min-w-[674px] large:!w-[80%] !px-10"
    >
      <div
        className={classNames(
          modalOpen
            ? 'w-full h-[30vh] regular:h-[40vh] large:h-[70vh] flex flex-col items-center justify-start'
            : 'flex items-center justify-center h-[40vh]'
        )}
      >
        {modalOpen ? (
          <div
            ref={playerContainerRef}
            className="flex flex-col w-full h-full items-center justify-center gap-y-5"
          >
            <ReactPlayer
              onLoad
              onPlay={onPlay}
              onPlaybackRateChange={(speed: number) => {
                playbackSpeedRef.current = speed || 1;
                emitVideoInteraction('speed', speed || 1);
              }}
              onProgress={(state: { playedSeconds: number }) => {
                currentTimeRef.current = state.playedSeconds || 0;
              }}
              onDuration={(duration: number) => {
                durationRef.current = duration;
              }}
              progressInterval={2000}
              controls
              width="100%"
              height="100%"
              url={videoUrl}
            />
            {type && type === 'emi' && description && (
              <Typography
                component="body-small"
                variant="regular"
                className="text-gray-650 self-start"
              >
                {description}
              </Typography>
            )}
          </div>
        ) : (
          <Loader className="self-center" />
        )}
      </div>
    </Dialog>
  );
};

export default VideoModal;
